import React from 'react'
import { ActivityIndicator, Platform, ScrollView, StyleSheet, View } from 'react-native'
import { useTheme } from '@react-navigation/native'
import QRCodeSVG from 'react-native-qrcode-svg'
import { Header, Icon, Screen, Text } from '@components'
import { useLanguage } from '@context'
import { Theme } from '@theme'
import { formatShortDate, getCommonStyles } from '@utils'
import { ScreenProps } from '@navigation'
import { useMyTicket } from '@hooks'

/**
 * `/events/:eventId/ticket` — "Mon billet" full screen: the same signed QR
 * shown on the event page, big enough to scan from across the door, plus who
 * it is for. Reached from the event page and from "Mes inscriptions". Once
 * opened, the data is cached to disk (its own persisted query root, see
 * `App.tsx`), so it still reads with no network at the door.
 */
export const MyTicket: React.FC<ScreenProps<'MyTicket'>> = ({ route, navigation }) => {
  const theme = useTheme()
  const { t, currentLanguage } = useLanguage()
  const styles = getStyles(theme)
  const eventId = String(route.params?.eventId ?? '')
  const { data, isLoading, isError } = useMyTicket(eventId)

  return (
    <Screen noPaddingTop>
      <Header
        title={t('events.myTicket.title')}
        iconLeft={Platform.OS === 'web' ? 'arrowLeft' : 'arrowBack'}
        // Opened by its address (a link, a reload) there is nothing to go back to.
        onPressLeft={() =>
          navigation.canGoBack()
            ? navigation.goBack()
            : navigation.navigate('Menu', { screen: 'Events' })
        }
      />
      {isLoading ? (
        <View style={styles.center}>
          <ActivityIndicator color={theme.colors.primary} />
        </View>
      ) : isError || !data ? (
        <View style={styles.center}>
          <Text type="titleBold" center>
            {t('events.myTicket.unavailableTitle')}
          </Text>
          <Text type="body" color={theme.colors.greyBlue} center style={styles.unavailableHint}>
            {t('events.myTicket.unavailableHint')}
          </Text>
        </View>
      ) : (
        <ScrollView contentContainerStyle={styles.scroll}>
          <View style={styles.card} testID="my-ticket-card">
            {data.ticketCode && data.event.phase !== 'ended' ? (
              <>
                <QRCodeSVG
                  value={data.ticketCode}
                  size={240}
                  color={theme.colors.primary}
                  backgroundColor={theme.colors.white}
                />
                <Text type="titleBold" center style={styles.name}>
                  {`${data.firstName} ${data.lastName}`.trim()}
                </Text>
                <Text type="bodyBold" center>
                  {data.event.name}
                </Text>
                <Text type="body" color={theme.colors.secondary} center>
                  {[
                    formatShortDate(data.event.startsAt, currentLanguage),
                    data.event.location,
                  ]
                    .filter(Boolean)
                    .join(' · ')}
                </Text>
                <Text type="bodyBold" color={theme.colors.primary} center style={styles.hint}>
                  {t('events.myTicket.presentAtDoor')}
                </Text>
              </>
            ) : (
              <>
                <Icon name="alertTriangle" size={48} color={theme.colors.greyBlue} />
                <Text type="titleBold" center>
                  {data.event.phase === 'ended'
                    ? t('events.myTicket.eventEnded')
                    : t('events.myTicket.unavailableTitle')}
                </Text>
              </>
            )}
          </View>
          {data.ticketCode && data.event.phase !== 'ended' ? (
            <View style={styles.brightness} testID="my-ticket-brightness-hint">
              <Icon name="alert" size={16} color={theme.colors.greyBlue} />
              <Text type="bodySmall" color={theme.colors.greyBlue} style={styles.brightnessText}>
                {t('events.myTicket.brightnessHint')}
              </Text>
            </View>
          ) : null}
        </ScrollView>
      )}
    </Screen>
  )
}

const getStyles = (theme: Theme) =>
  StyleSheet.create({
    center: {
      flex: 1,
      alignItems: 'center',
      justifyContent: 'center',
      padding: theme.spacing.xxl,
      gap: theme.spacing.sm,
    },
    unavailableHint: { lineHeight: 21 },
    scroll: {
      flexGrow: 1,
      alignItems: 'center',
      justifyContent: 'center',
      padding: theme.spacing.xl,
      gap: theme.spacing.lg,
    },
    card: {
      width: '100%',
      maxWidth: 380,
      backgroundColor: theme.colors.white,
      borderRadius: theme.border.jumbo,
      borderWidth: 1,
      borderColor: theme.colors.grey,
      padding: theme.spacing.xxl,
      alignItems: 'center',
      gap: theme.spacing.sm,
      ...getCommonStyles(theme).shadow,
    },
    name: { marginTop: theme.spacing.md },
    hint: { marginTop: theme.spacing.md },
    brightness: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: theme.spacing.xs,
      maxWidth: 380,
    },
    brightnessText: { flexShrink: 1, textAlign: 'center' },
  })
